// Bell icon for the Sidebar header — shows unread badge, opens a dropdown
// with the latest 50 notifications. Clicking an item marks it read and
// navigates to its link target.

import { useEffect, useRef, useState } from "preact/hooks";
import { createPortal } from "preact/compat";
import { Bell, CheckCheck } from "lucide-preact";
import { useLocation } from "wouter-preact";
import {
  markAllRead,
  markRead,
  useNotifications,
} from "../store/notifications";
import { inboxUnreadSig } from "../lib/inboxBadge";
import { toastError } from "../lib/toast";
import { openPersonalTaskSig, openProjectTaskSig } from "../store/deepLinks";
import type { NotificationItem } from "@juuplan/shared";
import { UserAvatar } from "./UserAvatar";
import { BotBadgeFor } from "./BotBadge";

function relTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diffMs = Date.now() - t;
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "gerade eben";
  const min = Math.round(sec / 60);
  if (min < 60) return `vor ${min} Min.`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `vor ${hr} Std.`;
  const days = Math.round(hr / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  return new Date(iso).toLocaleDateString("de-DE");
}

export function NotificationBell(): preact.JSX.Element {
  const state = useNotifications();
  // One unified badge across the app: the bell shows the SAME unread count as the
  // Inbox (and the sidebar/mobile nav), so processing an item in either surface
  // updates one number. The dropdown still lists notification rows (with avatar +
  // body); the count is the inbox truth.
  const unread = inboxUnreadSig.value;
  const [open, setOpen] = useState(false);
  const [, setLocation] = useLocation();
  const ref = useRef<HTMLDivElement | null>(null);
  // The dropdown is portaled to <body> (see below), so it is NOT a descendant
  // of `ref`. Track it separately so an outside-click check does not treat a
  // click inside the panel as "outside" and close it before the click lands.
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Escape. While open, move focus into the panel so
  // keyboard/AT users can reach the items; on close, put it back on the bell.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent): void => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      ref.current?.querySelector("button")?.focus();
    };
  }, [open]);

  // Ein Klick navigiert clientseitig. Zeigt der Link auf die Ansicht, in der wir
  // schon sind (gleicher Pfad), remountet nichts — der `?task=`-Parser der Route
  // läuft dann nie und die Aufgabe bliebe zu (wouter beobachtet nur den Pfad,
  // siehe store/deepLinks.ts). Deshalb wird die Aufgabe über das Deep-Link-Signal
  // geöffnet, das unabhängig vom Remount wirkt; navigiert wird dann ohne
  // `?task=`, damit kein verbrauchter Parameter in der URL hängen bleibt.
  const handleClick = (item: NotificationItem): void => {
    if (!item.read_at && !item.task_done) {
      // Nebenwirkung eines Klicks, der ohnehin navigiert — `markRead` läuft
      // mit `silentError` (store/notifications.ts) und toastet nicht selbst.
      // Absichtlich kein Toast bei Fehlschlag: die Navigation soll nicht auf
      // ein Detail wie den ungelesen-Zähler warten oder daran scheitern; ein
      // stehen gebliebenes Ungelesen-Abzeichen heilt sich beim nächsten Poll.
      void markRead(item.id).catch(() => {
        /* absichtlich still, siehe Kommentar oben */
      });
    }
    if (item.link) {
      const [path = "", query] = item.link.split("?");
      const raw = query ? new URLSearchParams(query).get("task") : null;
      const taskId = raw ? Number.parseInt(raw, 10) : Number.NaN;
      const sig = path.startsWith("/p/")
        ? openProjectTaskSig
        : path.startsWith("/lists/")
          ? openPersonalTaskSig
          : null;
      if (sig && !Number.isNaN(taskId)) {
        sig.value = taskId;
        setLocation(path);
      } else {
        setLocation(item.link);
      }
    }
    setOpen(false);
  };

  return (
    <div class="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        class="relative rounded p-1.5 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
        aria-label="Benachrichtigungen"
        aria-expanded={open}
      >
        <Bell size={16} aria-hidden="true" />
        {unread > 0 && (
          <span
            class="absolute -top-0.5 -right-0.5 inline-flex min-w-[16px] h-[16px] items-center justify-center rounded-full bg-[color:var(--color-error)] text-label font-semibold text-[color:var(--color-on-error)] px-1"
            aria-label={`${unread} ungelesen`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          class="fixed top-14 left-3 md:left-[252px] w-[360px] max-w-[calc(100vw-24px)] max-h-[70vh] overflow-auto rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] shadow-level-3 z-50 focus:outline-none"
          role="dialog"
          aria-label="Benachrichtigungen"
          tabIndex={-1}
        >
          <header class="sticky top-0 flex items-center justify-between gap-2 px-3 py-2 border-b border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)]">
            <span class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
              Benachrichtigungen
            </span>
            {unread > 0 && (
              <button
                type="button"
                onClick={() =>
                  // `markAllRead` läuft mit `silentError` (store/notifications.ts)
                  // und toastet selbst nicht — das ist hier aber ein explizit
                  // angeklickter Schreibvorgang, der eine Rückmeldung braucht.
                  void markAllRead().catch(() =>
                    toastError("Konnte nicht als gelesen markiert werden.")
                  )
                }
                class="inline-flex items-center gap-1 rounded px-2 py-1 text-label font-medium text-[color:var(--color-primary)] hover:bg-[color:var(--color-surface-container-low)] transition-colors"
              >
                <CheckCheck size={14} aria-hidden="true" />
                Alle gelesen
              </button>
            )}
          </header>

          {state.items.length === 0 ? (
            <div class="px-3 py-6 text-center text-xs text-[color:var(--color-on-surface-variant)]">
              {state.loading ? "Lädt…" : "Keine Benachrichtigungen"}
            </div>
          ) : (
            <ul>
              {state.items.map((item) => {
                // Eine Benachrichtigung zu einer erledigten Aufgabe ist kein
                // offener Posten mehr: keine Hervorhebung, kein Punkt, kein
                // Zaehler (der kommt ohnehin aus dem Posteingang, der dieselbe
                // Regel anwendet). Der Eintrag bleibt aber stehen und anklickbar
                // — er ist die Vorgeschichte der Aufgabe, kein Muell.
                const unread = !item.read_at && !item.task_done;
                return (
                  <li
                    key={item.id}
                    class={`border-b border-[color:var(--color-outline-variant)] last:border-0 ${
                      unread ? "bg-[color:var(--color-primary-container)]/20" : ""
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => handleClick(item)}
                      class="w-full text-left flex gap-2 px-3 py-2 hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus:bg-[color:var(--color-surface-container-low)]"
                    >
                      {item.actor_id ? (
                        <UserAvatar userId={item.actor_id} size={24} />
                      ) : (
                        <span class="size-6 shrink-0 rounded-full bg-[color:var(--color-surface-container-high)]" aria-hidden="true" />
                      )}
                      <span class="flex-1 min-w-0">
                        <span class="block text-xs font-medium text-[color:var(--color-on-surface)] leading-tight">
                          {item.title}
                        </span>
                        {item.body && (
                          <span class="block text-micro text-[color:var(--color-on-surface-variant)] mt-0.5 truncate whitespace-pre">
                            {item.body}
                          </span>
                        )}
                        <span class="flex items-center gap-1 text-label text-[color:var(--color-on-surface-variant)] mt-0.5">
                          {relTime(item.created_at)}
                          {item.task_done && <span>· erledigt</span>}
                          {/* Auslöser war ein Bot — im Titel steckt nur sein
                              Name, ohne Hinweis auf die Automatisierung. */}
                          <BotBadgeFor userId={item.actor_id} />
                        </span>
                      </span>
                      {unread && (
                        <span
                          class="size-1.5 mt-1.5 shrink-0 rounded-full bg-[color:var(--color-primary)]"
                          aria-label="ungelesen"
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <footer class="sticky bottom-0 px-3 py-2 border-t border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] text-center">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setLocation("/settings");
              }}
              class="text-label text-[color:var(--color-on-surface-variant)] hover:text-[color:var(--color-primary)] transition-colors"
            >
              Einstellungen
            </button>
          </footer>
        </div>,
        document.body
      )}
    </div>
  );
}
